import { Link } from "@tanstack/react-router";
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Folder,
  FolderInput,
  Clock3,
} from "lucide-react";
import { Button } from "@/components/ui";
import type { MailIntakeItem } from "@/lib/data/schemas/mail-intake";
import { isMailIntakeActive } from "@/lib/data/schemas/mail-intake";

const labels: Record<MailIntakeItem["status"], string> = {
  uploading: "Uploading",
  queued: "Queued",
  preparing: "Preparing",
  processing_pages: "Reading & tagging",
  describing: "Summarizing",
  sorting: "Finding a folder",
  filing: "Filing",
  filed: "Filed",
  needs_attention: "Needs attention",
  failed: "Could not finish",
};

export function MailIntakeRow({
  item,
  onChooseFolder,
}: {
  item: MailIntakeItem;
  onChooseFolder: () => void;
}) {
  const active = isMailIntakeActive(item);
  const attention =
    item.status === "needs_attention" || item.status === "failed";
  const destination = item.destination;
  const uploadedAt = new Date(item.dateCreated).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const uploadDetails = item.uploadedByDisplayName
    ? `${uploadedAt} · Uploaded by ${item.uploadedByDisplayName}`
    : uploadedAt;
  const location = destination
    ? [destination.matterName ?? "Firm Files", destination.folderName].join(
        " / ",
      )
    : item.status === "filed"
      ? (item.message ?? "Filed to an existing folder")
      : "";
  return (
    <div className="grid gap-3 border-b px-5 py-4 last:border-b-0 lg:grid-cols-[minmax(180px,1.1fr)_minmax(180px,.8fr)_minmax(220px,1.2fr)_auto] lg:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
          <FileText className="size-4" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium" title={item.name}>
            {item.name}
          </p>
          <p
            className="text-muted-foreground mt-0.5 truncate text-xs"
            title={uploadDetails}
          >
            {uploadDetails}
          </p>
        </div>
      </div>
      <div className="min-w-0">
        <div
          className={`flex items-center gap-1.5 text-xs font-medium ${attention ? "text-amber-700 dark:text-amber-400" : item.status === "filed" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}
        >
          {active ? (
            <Clock3 className="size-3.5" />
          ) : attention ? (
            <AlertCircle className="size-3.5" />
          ) : (
            <CheckCircle2 className="size-3.5" />
          )}
          {labels[item.status]}
          {item.status === "processing_pages" && item.totalPages ? (
            <span className="ml-auto font-normal">
              {item.processedPages}/{item.totalPages} pages
            </span>
          ) : null}
        </div>
        {active ? (
          <div
            className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full"
            role="progressbar"
            aria-label={`Processing ${item.name}`}
            aria-valuenow={item.progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-700"
              style={{ width: `${item.progress}%` }}
            />
          </div>
        ) : null}
      </div>
      <div className="min-w-0 text-xs">
        {attention ? (
          <p className="text-muted-foreground leading-5">
            {item.message ??
              "Choose an existing folder to finish filing this document."}
          </p>
        ) : destination?.matterId ? (
          <Link
            to="/matters/$matterId/files"
            params={{ matterId: destination.matterId }}
            search={{ selectedId: destination.folderId }}
            className="text-primary inline-flex max-w-full items-center gap-1.5 hover:underline"
          >
            <Folder className="size-3.5 shrink-0" />
            <span className="truncate" title={location}>
              {location}
            </span>
          </Link>
        ) : destination ? (
          <Link
            to="/files"
            className="text-primary inline-flex max-w-full items-center gap-1.5 hover:underline"
          >
            <Folder className="size-3.5 shrink-0" />
            <span className="truncate">{location}</span>
          </Link>
        ) : location ? (
          <p className="text-muted-foreground">{location}</p>
        ) : null}
      </div>
      <div className="flex justify-end gap-1.5">
        {(attention || item.status === "filed") &&
        item.canMove &&
        item.fileId &&
        item.revision ? (
          <Button size="sm" variant="ghost" onClick={onChooseFolder}>
            <FolderInput aria-hidden="true" />
            Move to folder
          </Button>
        ) : null}
      </div>
    </div>
  );
}
